import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Employee.css";

const STAFF_API = "https://salathai.co.tz/mrms/Staff.php";
const ITEMS_PER_PAGE = 8;
const ROLES = ["Admin", "Cashier", "Waitress", "Cleaner", "Chef"];

export default function Employee() {
  const token = localStorage.getItem("token");

  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    full_name: "",
    role: "",
    phone_number: "",
    email: "",
    password: "",
    pin: "",
    contract: "yes"
  });

  useEffect(() => {
    if (!token) {
      Swal.fire("Session Expired", "Login again", "warning");
      return;
    }
    loadStaff();
  }, []);

  useEffect(() => {
    filterStaff();
  }, [staffList, activeFilter, searchTerm]);

  const loadStaff = async () => {
    try {
      const res = await fetch(STAFF_API);
      const data = await res.json();
      setStaffList(data);
    } catch {
      Swal.fire("Error", "Failed to load staff", "error");
    }
  };

  const filterStaff = () => {
    let filtered = [...staffList];

    // Filter by contract type
    if (activeFilter === "contract") {
      filtered = filtered.filter(s => s.contract === "yes");
    } else if (activeFilter === "non-contract") {
      filtered = filtered.filter(s => s.contract === "no");
    }

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(s => 
        s.full_name?.toLowerCase().includes(term) ||
        s.role?.toLowerCase().includes(term) ||
        s.phone_number?.includes(term)
      );
    }

    setFilteredStaff(filtered);
    setCurrentPage(1);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({ 
      full_name: "", 
      role: "", 
      phone_number: "", 
      email: "", 
      password: "", 
      pin: "",
      contract: "yes" 
    });
    setEditingStaffId(null);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.full_name || !formData.role || !formData.phone_number) {
      return Swal.fire("Missing fields", "Please fill all required fields", "warning");
    }

    if (formData.role === "Waitress" && !formData.pin) {
      return Swal.fire("PIN required", "Waitress must have a PIN", "warning");
    }

    const payload = {
      ...formData,
      email: formData.role === "Waitress" ? null : formData.email,
      password: formData.role === "Waitress" ? null : formData.password,
      pin: formData.role === "Waitress" ? formData.pin : null
    };

    try {
      const res = await fetch(
        editingStaffId ? `${STAFF_API}?id=${editingStaffId}` : STAFF_API,
        {
          method: editingStaffId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      const data = await res.json();
      Swal.fire("Success", data.message || "Staff saved successfully", "success");
      setShowModal(false);
      resetForm();
      loadStaff();
    } catch {
      Swal.fire("Error", "Failed to save staff", "error");
    }
  };

  const handleDelete = async staff => {
    const confirm = await Swal.fire({
      title: "Delete Staff?",
      text: `Are you sure you want to delete ${staff.full_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete"
    });
    
    if (!confirm.isConfirmed) return;

    try {
      await fetch(`${STAFF_API}?id=${staff.id}`, { method: "DELETE" });
      Swal.fire("Deleted!", "Staff has been removed.", "success");
      loadStaff();
    } catch {
      Swal.fire("Error", "Failed to delete staff", "error");
    }
  };

  const toggleActive = async staff => {
    try {
      await fetch(`${STAFF_API}?id=${staff.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...staff,
          active: staff.active === "yes" ? "no" : "yes"
        })
      });
      loadStaff();
    } catch {
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const totalPages = Math.ceil(filteredStaff.length / ITEMS_PER_PAGE);
  const pageData = filteredStaff.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getStatusBadgeClass = (status) => {
    if (status === "yes") return "badge-active";
    return "badge-inactive";
  };

  const getContractBadgeClass = (contract) => {
    if (contract === "yes") return "badge-contract";
    return "badge-noncontract";
  };

  return (
    <div className="employee-container">
      {/* Header */}
      <div className="employee-header">
        <div className="employee-header-left">
          <h2>Employee Management</h2>
          <span className="employee-count">{filteredStaff.length} Total</span>
        </div>
        <button 
          className="employee-add-btn" 
          onClick={() => { resetForm(); setShowModal(true); }}
        >
          + Add Employee
        </button>
      </div>

      {/* Filters */}
      <div className="employee-filters">
        <div className="employee-filter-tabs">
          <button 
            className={`filter-tab ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => setActiveFilter("all")}
          >
            All
          </button>
          <button 
            className={`filter-tab ${activeFilter === "contract" ? "active" : ""}`}
            onClick={() => setActiveFilter("contract")}
          >
            Contract
          </button>
          <button 
            className={`filter-tab ${activeFilter === "non-contract" ? "active" : ""}`}
            onClick={() => setActiveFilter("non-contract")}
          >
            Non-Contract
          </button>
        </div>
        <div className="employee-search">
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="employee-table-wrapper">
        <table className="employee-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Phone</th>
              <th>Contract</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.length > 0 ? (
              pageData.map(staff => (
                <tr key={staff.id}>
                  <td className="employee-name">{staff.full_name}</td>
                  <td><span className={`role-badge role-${staff.role?.toLowerCase()}`}>{staff.role}</span></td>
                  <td>{staff.phone_number}</td>
                  <td>
                    <span className={`contract-badge ${getContractBadgeClass(staff.contract)}`}>
                      {staff.contract === "yes" ? "Contract" : "Non-Contract"}
                    </span>
                  </td>
                  <td>
                    <span 
                      className={`status-badge ${getStatusBadgeClass(staff.active)}`}
                      onClick={() => toggleActive(staff)}
                    >
                      {staff.active === "yes" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="employee-actions">
                    <button 
                      className="action-btn edit-btn"
                      onClick={() => { 
                        setEditingStaffId(staff.id); 
                        setFormData(staff); 
                        setShowModal(true); 
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(staff)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="employee-empty">
                  No employees found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="employee-pagination">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="employee-modal">
          <div className="employee-modal-content">
            <div className="employee-modal-header">
              <h3>{editingStaffId ? "Edit Employee" : "Add New Employee"}</h3>
              <button 
                className="employee-modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="employee-modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    name="full_name"
                    placeholder="Enter full name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Role *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Role</option>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    name="phone_number"
                    placeholder="Enter phone number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Contract Type *</label>
                  <select
                    name="contract"
                    value={formData.contract}
                    onChange={handleChange}
                    required
                  >
                    <option value="yes">Contract</option>
                    <option value="no">Non-Contract</option>
                  </select>
                </div>

                {formData.role === "Waitress" ? (
                  <div className="form-group full-width">
                    <label>PIN *</label>
                    <input
                      name="pin"
                      type="password"
                      placeholder="Enter 4-digit PIN"
                      value={formData.pin}
                      onChange={handleChange}
                      maxLength={4}
                      required={formData.role === "Waitress"}
                    />
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        name="email"
                        type="email"
                        placeholder="Enter email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Password</label>
                      <input
                        name="password"
                        type="password"
                        placeholder="Enter password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="employee-modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingStaffId ? "Update Employee" : "Add Employee"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}